import { Card, Form, Input, Select } from "antd";
import { useQuote } from "../QuoteProvider";
import { StyledFormItem } from "../StyledQuoteInput";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import { useIsMobile } from "../MobileBreakpointContext";
import { useTranslationNamespace } from "../../../hooks/useTranslationNamespace";
import "./scss/step-readiness.scss";

type StepReadinessTimelineProps = {
  next: (override?: boolean) => void;
};

// Option values are persisted as-is in the Quote document, so they must stay
// stable across language changes — only the label is translated.
const READINESS_OPTIONS = [
  { value: "Yes, I'm ready", labelKey: "patientReadiness.ready" },
  { value: "I'm considering", labelKey: "patientReadiness.considering" },
  { value: "Just exploring", labelKey: "patientReadiness.exploring" },
];

const SURGERY_DATE_OPTIONS = [
  { value: "Within 30 days", labelKey: "surgeryDateRange.within30" },
  { value: "1 to 3 months", labelKey: "surgeryDateRange.oneToThreeMonths" },
  { value: "3 to 6 months", labelKey: "surgeryDateRange.threeToSixMonths" },
  { value: "Just Researching", labelKey: "surgeryDateRange.researching" },
];

const BUDGET_OPTIONS = [
  { value: "$11,000+", labelKey: "budget.high" },
  { value: "$8,000 to $11,000", labelKey: "budget.mid" },
  { value: "$5,000 to $8,000", labelKey: "budget.low" },
];

const TRAVEL_OPTIONS = [
  { value: "Yes", labelKey: "willingToTravel.yes" },
  { value: "Maybe", labelKey: "willingToTravel.maybe" },
  { value: "No", labelKey: "willingToTravel.no" },
];

const Question = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <StyledFormItem className="step-readiness__question">
    <div className="step-readiness__question-label">
      {label}
      {required && <span className="step-readiness__required">*</span>}
    </div>
    {children}
  </StyledFormItem>
);

const StepReadinessTimeline = ({ next }: StepReadinessTimelineProps) => {
  const quoteData = useQuote();
  const isMobile = useIsMobile();
  const { t } = useTranslationNamespace("quote");
  const [form] = Form.useForm();

  return (
    <div className="step-readiness">
      {!isMobile && (
        <h1 className="step-readiness__title">{t("readiness.title")}</h1>
      )}

      <div className="step-readiness__frame">
        <Card bordered={!isMobile} className="step-readiness__card">
          <Form layout="vertical" form={form}>
            <Question
              label={t("readiness.patientReadiness.question")}
              required
            >
              <Select
                size="large"
                className="readiness-select"
                style={{ width: "100%" }}
                value={quoteData.patientReadiness || undefined}
                onChange={(v) => quoteData.setPatientReadiness(v)}
                placeholder={t("readiness.selectPlaceholder")}
                options={READINESS_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(`readiness.${o.labelKey}`),
                }))}
              />
            </Question>

            <Question label={t("readiness.surgeryDateRange.question")}>
              <Select
                size="large"
                className="readiness-select"
                style={{ width: "100%" }}
                value={quoteData.surgeryDateRange || undefined}
                onChange={(v) => quoteData.setSurgeryDateRange(v)}
                placeholder={t("readiness.selectPlaceholder")}
                options={SURGERY_DATE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(`readiness.${o.labelKey}`),
                }))}
              />
            </Question>

            <Question label={t("readiness.budget.question")}>
              <Select
                size="large"
                className="readiness-select"
                style={{ width: "100%" }}
                value={quoteData.budget || undefined}
                onChange={(v) => quoteData.setBudget(v)}
                placeholder={t("readiness.selectPlaceholder")}
                options={BUDGET_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(`readiness.${o.labelKey}`),
                }))}
              />
            </Question>

            <Question label={t("readiness.willingToTravel.question")}>
              <Select
                size="large"
                className="readiness-select"
                style={{ width: "100%" }}
                value={quoteData.willingToTravel || undefined}
                onChange={(v) => quoteData.setWillingToTravel(v)}
                placeholder={t("readiness.selectPlaceholder")}
                options={TRAVEL_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(`readiness.${o.labelKey}`),
                }))}
              />
            </Question>

            <Question label={t("readiness.patientNotes.question")}>
              <Input.TextArea
                rows={3}
                className="step-readiness__notes"
                value={quoteData.patientNotes}
                onChange={(e) => quoteData.setPatientNotes(e.target.value)}
                placeholder={t("readiness.patientNotes.placeholder")}
              />
            </Question>
          </Form>
        </Card>
      </div>

      <div className="quote-button-mobile-container">
        <QuoteButton onClick={next}>{t("readiness.continue")}</QuoteButton>
      </div>
    </div>
  );
};

export default StepReadinessTimeline;
